<!-- A word in a sentence that has an entry in the glossary.

     `<TermLink id="clv">CLV</TermLink>` — the word keeps its place in the
     prose and picks up a dotted underline, which is the printed convention for
     "there is a definition of this" and not the solid one the app already uses
     for an action. With no `id` it opens the glossary at the top, which is what
     the "Glossary ›" line at the foot of each help modal wants.

     A BUTTON, not a link, for the reason `.link` in app.css gives: it navigates
     in-app rather than to a URL, and an <a> would be a lie to a screen reader.

     IT DISMISSES THE MODAL IT IS IN. Five of the six places this appears are
     inside an IonModal, and a modal is on top of the router outlet — pushing
     the glossary under an open modal would leave the reader staring at the
     help they just tapped away from. `closest('ion-modal')` finds whichever
     one it is in without this file knowing anything about it, and dismissing
     fires the `didDismiss` those modals already close themselves on.

     NOTHING IS IMPORTED FROM `lib/glossary.js`. This component is used inside
     Today's "How it works", and Today's shell is the screen the Lighthouse run
     measures; importing the table for a fallback label would put every
     definition in the app into that chunk. -->
<template>
  <button type="button" class="term" :aria-label="label" @click="go">
    <slot />
  </button>
</template>

<script setup>
import { computed, useSlots } from 'vue';
import { useRouter } from 'vue-router';

const props = defineProps({
  /** The glossary entry to land on. Omitted means the whole glossary. */
  id: { type: String, default: '' },
});

const router = useRouter();
const slots = useSlots();

/** The words between the tags, so the label reads as the sentence does. */
function textOf(nodes) {
  return nodes.map((n) => {
    if (typeof n.children === 'string') return n.children;
    return Array.isArray(n.children) ? textOf(n.children) : '';
  }).join('').trim();
}

const label = computed(() => {
  const text = textOf(slots.default?.() ?? []);
  return props.id && text ? `What is ${text}?` : 'Open the glossary';
});

async function go(event) {
  const modal = event.currentTarget?.closest?.('ion-modal');
  if (modal?.dismiss) await modal.dismiss();
  // A duplicate navigation (the same term, tapped twice) resolves to a failure
  // rather than an error; nothing here needs to know either way.
  await router.push({ name: 'glossary', hash: props.id ? `#${props.id}` : '' }).catch(() => {});
}
</script>

<style scoped>
/* Inline, so it wraps with the sentence it sits in rather than breaking it.
   Dotted where `.link` is solid, and always carbon: a term inside a line of
   small print has to be the darker thing on the line to read as a link. */
.term {
  display: inline;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--carbon);
  font: inherit;
  font-weight: 700;
  text-decoration: underline dotted;
  text-decoration-thickness: 1px;
  text-underline-offset: 0.2em;
  text-align: inherit;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
</style>
